import { useState } from 'react';
import { Archive, ArchiveRestore, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  useArchiveEquipmentModel,
  useCreateEquipmentModel,
  useEquipmentModel,
  useRestoreEquipmentModel,
  useUpdateEquipmentModel,
} from '../hooks/useEquipment';
import { useEquipmentPermissions } from '../hooks/useEquipmentPermissions';
import type { EquipmentKind, EquipmentModelInput } from '../types';
import { InstallGuideBadge } from './InstallGuideBadge';
import { CreateModelForm, EquipmentOverviewTab } from './tabs/EquipmentOverviewTab';
import { EquipmentInstallGuideTab } from './tabs/EquipmentInstallGuideTab';
import { EquipmentDocumentsTab } from './tabs/EquipmentDocumentsTab';
import { EquipmentPhotosTab } from './tabs/EquipmentPhotosTab';
import { EquipmentSensorsTab } from './tabs/EquipmentSensorsTab';

type PanelTab = 'overview' | 'installGuide' | 'documents' | 'photos' | 'sensors';

const TABS: { value: PanelTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'installGuide', label: 'Install guide' },
  { value: 'documents', label: 'Documents' },
  { value: 'photos', label: 'Photos' },
  { value: 'sensors', label: 'Sensors' },
];

interface EquipmentDetailPanelProps {
  modelId: string | null;
  mode: 'view' | 'create';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

const EMPTY_DRAFT = { brand: '', model: '', label: '', kind: null as EquipmentKind | null, labelTouched: false };

export function EquipmentDetailPanel({ modelId, mode, open, onOpenChange, onCreated }: EquipmentDetailPanelProps) {
  const isCreate = mode === 'create';
  const { canWrite } = useEquipmentPermissions();
  const { data: detail, isLoading } = useEquipmentModel(isCreate ? null : modelId);
  const updateModel = useUpdateEquipmentModel();
  const createModel = useCreateEquipmentModel();
  const archiveModel = useArchiveEquipmentModel();
  const restoreModel = useRestoreEquipmentModel();
  const [activeTab, setActiveTab] = useState<PanelTab>('overview');
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  if (!open) return null;

  const model = detail?.model;
  const save = (data: Partial<EquipmentModelInput>) => {
    if (!modelId || isCreate || !canWrite) return;
    updateModel.mutate({ id: modelId, data });
  };

  const canCreate = isCreate && draft.brand.trim() && draft.model.trim() && draft.label.trim();
  const handleCreate = async () => {
    if (!canCreate) return;
    try {
      const created = await createModel.mutateAsync({
        brand: draft.brand.trim(), model: draft.model.trim(), label: draft.label.trim(), kind: draft.kind,
      });
      setDraft(EMPTY_DRAFT);
      onCreated?.(created.id);
    } catch { /* toast from the mutation */ }
  };

  return (
    <div data-testid="equipment-panel" className="h-full flex flex-col border-l bg-background overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold truncate">{isCreate ? 'New equipment model' : model?.label ?? 'Equipment model'}</span>
          {model && <InstallGuideBadge status={model.installGuideStatus} />}
          {model?.status === 'archived' && <span className="text-xs text-muted-foreground">Archived</span>}
        </div>
        <div className="flex items-center gap-1">
          {model && canWrite && (model.status === 'active' ? (
            <Button variant="ghost" size="sm" onClick={() => archiveModel.mutate(model.id)} disabled={archiveModel.isPending}>
              <Archive className="h-4 w-4 mr-1" /> Archive
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => restoreModel.mutate(model.id)} disabled={restoreModel.isPending}>
              <ArchiveRestore className="h-4 w-4 mr-1" /> Restore
            </Button>
          ))}
          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Close panel" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-0 border-b px-2 shrink-0 overflow-x-auto">
        {TABS.map((tab) => {
          const disabled = isCreate && tab.value !== 'overview';
          return (
            <button key={tab.value} type="button" disabled={disabled} onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                activeTab === tab.value ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
                disabled && 'opacity-40 cursor-not-allowed hover:text-muted-foreground',
              )}>
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {!isCreate && isLoading && (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        )}
        {!isCreate && !isLoading && !detail && <p className="text-sm text-muted-foreground py-8 text-center">Model not found.</p>}
        {isCreate && <CreateModelForm value={draft} onChange={setDraft} />}
        {!isCreate && detail && model && (
          <>
            {activeTab === 'overview' && <EquipmentOverviewTab model={model} aliases={detail.aliases} canWrite={canWrite} onSave={save} />}
            {activeTab === 'installGuide' && <EquipmentInstallGuideTab model={model} canWrite={canWrite} onSave={save} />}
            {activeTab === 'documents' && <EquipmentDocumentsTab modelId={model.id} documents={detail.documents} canWrite={canWrite} />}
            {activeTab === 'photos' && <EquipmentPhotosTab modelId={model.id} photos={detail.photos} canWrite={canWrite} />}
            {activeTab === 'sensors' && <EquipmentSensorsTab modelId={model.id} />}
          </>
        )}
      </div>

      {isCreate && (
        <div className="px-4 py-3 border-t shrink-0">
          <Button className="w-full" disabled={!canCreate || createModel.isPending} onClick={handleCreate}>Create model</Button>
        </div>
      )}
    </div>
  );
}
